import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import {
  fromAWSDate,
  toAWSDate,
} from "@examspark/common/dist/utils/coursePlanner";
import {
  NoResults,
  useMutationCreatePlannerItem,
  useMutationDeletePlannerItem,
  useQueryGetClassroom,
  useQueryGetClassroomCourseCalendar,
  useQueryGetUserClassrooms,
  useQueryListClassroomPlannerItems,
  useAppContext,
} from "@examspark/common-app";
import { DismissButton, SelectInput, toast, VFlex } from "@examspark/common-ui";
import { IconCalendarLine } from "@examspark/common-ui/dist/components/icons";
import { AnyResource, ParsedClassroom, Resource } from "@examspark/types";

import Loading from "../../common/Loading";
import Modal, { ModalProps } from "../../common/Modal";
import { ClassroomPlannerPicker } from "../planners/ClassroomPlannerCalendar";

export const getParsedDate = (date?: Date) => {
  if (date === undefined) return;
  // Get year, month, and day part from the date
  const year = date.toLocaleString("default", {
    year: "numeric",
  });
  const month = date.toLocaleString("default", {
    month: "short",
  });
  const day = date.toLocaleString("default", {
    day: "2-digit",
  });
  const weekday = date.toLocaleString("default", {
    weekday: "short",
  });

  return {
    year,
    month,
    day,
    weekday,
  };
};

const StyledModalScroll = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const StyledModayBody = styled(VFlex)`
  gap: 1rem;
  padding: 1rem;
`;

const PinResourceToDateModal = (
  props: ModalProps & {
    unitId?: string;
    resource?: AnyResource;
    classroom?: ParsedClassroom;
    onComplete?: () => void;
  },
) => {
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [state] = useAppContext();
  const queryClient = useQueryClient();
  const createPlannerItem = useMutationCreatePlannerItem();
  const deletePlannerItem = useMutationDeletePlannerItem();
  const { data: classrooms } = useQueryGetUserClassrooms(
    state.workspaceUser?.id,
    state.user?.id,
  );
  const [classroomId, setClassroomId] = useState<string | undefined | null>(
    props.classroom?.id || undefined,
  );
  const { data: classroom, isLoading } = useQueryGetClassroom(classroomId);
  const { data: plannerItems } = useQueryListClassroomPlannerItems(classroomId);

  useEffect(() => {
    if (!props.isOpen) return;
    if (!props.resource?.id) return;

    const dates =
      plannerItems
        ?.filter((item) => {
          const [, , resourceId] = item.sk.split(":");
          return resourceId === `${props.resource?.id}/${props.resource?.sk}`;
        })
        .map((item) => {
          const [, date] = item.sk.split(":");
          return fromAWSDate(date);
        }) ?? [];
    setSelectedDates(dates);
  }, [props.resource?.id, props.resource?.sk, plannerItems, props.isOpen]);

  const courseCalendar = useQueryGetClassroomCourseCalendar(classroom).data;

  const handleSelect = async (date: Date) => {
    if (!props.resource?.id) {
      console.error("no resource id");
      return;
    }

    if (!classroom?.id) {
      console.error("no classroom id");
      return;
    }

    const newDates = [...selectedDates];
    const index = newDates.findIndex((d) => toAWSDate(d) === toAWSDate(date));

    if (index > -1) {
      newDates.splice(index, 1);
      try {
        const res = await deletePlannerItem.mutateAsync({
          id: classroom.id,
          sk: `classroom-planner-item:${toAWSDate(date)}:${props.resource.id}/${
            props.resource.sk
          }`,
          workspaceId: classroom.workspaceId,
        });

        queryClient.setQueriesData(
          ["classroom", classroom.id, "planner-items"],
          (old: Resource[] | undefined) => {
            if (!old) return old;

            return old.filter((r) => {
              return !(
                r.id === res.data?.deleteResource.id &&
                r.sk === res.data?.deleteResource.sk
              );
            });
          },
        );

        toast.success("Planner item deleted");
      } catch (error) {
        toast.error("Error deleting planner item");

        console.error(error);
        return;
      }
    } else {
      newDates.push(date);

      try {
        const res = await createPlannerItem.mutateAsync({
          id: classroom.id,
          sk: `classroom-planner-item:${toAWSDate(date)}:${props.resource.id}/${
            props.resource.sk
          }`,
          schemaVersion: "2020-01-01",
          resourceId: `${props.resource.id}/${props.resource.sk}`,
          workspaceId: classroom.workspaceId,
        });

        queryClient.setQueriesData(
          ["classroom", classroom.id, "planner-items"],
          (old: Resource[] | undefined) => {
            if (!old) return old;
            if (!res.data) return old;

            const plannerItem = {
              ...res.data?.createResource,
              resource: props.resource as Resource,
            };

            return [...old, plannerItem];
          },
        );

        toast.success("Planner item created");
      } catch (error) {
        toast.error("Error creating planner item");

        console.error(error);
        return;
      }
    }

    setSelectedDates(newDates);
  };

  useEffect(() => {
    if (!classroomId && !!classrooms?.length) {
      setClassroomId(classrooms[0].classroom?.id);
    }
  }, [classrooms, classroomId]);

  return (
    <Modal
      title="Pin Resource to Date"
      size="large"
      {...props}
      buttons={<DismissButton onDismiss={props.onRequestClose} />}
      flexBodyColumn
    >
      <StyledModayBody>
        <SelectInput
          value={classroomId ?? undefined}
          onChange={(e) => {
            setClassroomId(e?.value);
          }}
          menuPortalTarget={document.body}
          options={classrooms
            ?.filter((classUser) => !!classUser.classroom)
            ?.map((classroom) => ({
              label: classroom.classroom?.name ?? "",
              value: classroom.classroom?.id ?? "",
            }))}
        />
        <StyledModalScroll>
          {isLoading && <Loading>Loading Classroom Plan</Loading>}
          {courseCalendar == null ? (
            <NoResults icon={<IconCalendarLine />}>
              To pin resources to dates, please choose a calendar in this
              classroom's settings.
            </NoResults>
          ) : (
            <ClassroomPlannerPicker
              courseCalendar={courseCalendar}
              onDayClick={handleSelect}
              selectedDates={selectedDates}
              onlyShowPinsFor={{
                resource: props.resource
                  ? [props.resource.id, props.resource.sk].join("/")
                  : undefined,
                classroom: classroom?.id,
              }}
            />
          )}
        </StyledModalScroll>
      </StyledModayBody>
    </Modal>
  );
};

export default PinResourceToDateModal;
